import React, { useState } from 'react';
import { Container, Table, Alert, Form, Row, Col } from 'react-bootstrap';
import { SectionLoader } from '../../../Components/LoadingSpinner';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { useInventorySessions, BuildingSessionSummary } from '../../../hooks/superadmin/useInventorySessions';
import SuperadminBreadcrumb from '../SuperadminBreadcrumb';
import './InventoryTimesPage.css';

const InventoryTimesPage: React.FC = () => {
    useDocumentTitle('Inventeringstider');

    // Default to last 7 days
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const formatDateForInput = (date: Date): string => {
        return date.toISOString().split('T')[0];
    };

    const [startDate, setStartDate] = useState(formatDateForInput(weekAgo));
    const [endDate, setEndDate] = useState(formatDateForInput(today));
    const [expandedBuildings, setExpandedBuildings] = useState<Set<string>>(new Set());

    const { summaries, loading, error } = useInventorySessions(startDate, endDate);

    const formatDuration = (minutes: number): string => {
        if (minutes === 0) return '0m';
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        if (hours === 0) return `${mins}m`;
        if (mins === 0) return `${hours}h`;
        return `${hours}h ${mins}m`;
    };

    const toggleExpanded = (buildingKey: string) => {
        setExpandedBuildings((prev) => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(buildingKey)) {
                newExpanded.delete(buildingKey);
            } else {
                newExpanded.add(buildingKey);
            }
            return newExpanded;
        });
    };

    const renderBuildingRow = (summary: BuildingSessionSummary) => {
        const isExpanded = expandedBuildings.has(summary.buildingKey);

        return (
            <React.Fragment key={summary.buildingKey}>
                <tr
                    className="building-row"
                    onClick={() => toggleExpanded(summary.buildingKey)}
                >
                    <td className="building-name-cell">
                        {isExpanded ? (
                            <ChevronDown size={16} className="expand-icon" />
                        ) : (
                            <ChevronRight size={16} className="expand-icon" />
                        )}
                        <span className="building-name">{summary.buildingName}</span>
                    </td>
                    <td>{formatDuration(summary.totalMinutes)}</td>
                    <td>{summary.sessionCount} sessioner</td>
                </tr>
                {isExpanded &&
                    summary.dailyBreakdown.map((day) => (
                        <tr
                            key={`${summary.buildingKey}-${day.date}`}
                            className="day-row"
                        >
                            <td className="day-date-cell">{day.date}</td>
                            <td>{formatDuration(day.minutes)}</td>
                            <td>{day.sessionCount} sessioner</td>
                        </tr>
                    ))}
            </React.Fragment>
        );
    };

    return (
        <>
        <SuperadminBreadcrumb title="Inventeringstider" />
        <Container className="inventory-times-page">
            <h1 className="page-title">Inventeringstider</h1>

            <Row className="date-filter-row mb-4">
                <Col xs={12} md={6} lg={3}>
                    <Form.Group>
                        <Form.Label>Från</Form.Label>
                        <Form.Control
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col xs={12} md={6} lg={3}>
                    <Form.Group>
                        <Form.Label>Till</Form.Label>
                        <Form.Control
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </Form.Group>
                </Col>
            </Row>

            {loading && <SectionLoader />}

            {error && <Alert variant="danger">{error}</Alert>}

            {!loading && !error && summaries.length === 0 && (
                <Alert variant="info">
                    Inga inventeringssessioner hittades för valt datumintervall.
                </Alert>
            )}

            {!loading && !error && summaries.length > 0 && (
                <Table striped bordered hover className="sessions-table">
                    <thead>
                        <tr>
                            <th>Byggnad</th>
                            <th>Total tid</th>
                            <th>Sessioner</th>
                        </tr>
                    </thead>
                    <tbody>{summaries.map(renderBuildingRow)}</tbody>
                </Table>
            )}
        </Container>
        </>
    );
};

export default InventoryTimesPage;
